import { getTaskVigenteLeadModalStyle, getTaskTypeLabel, formatTaskTitleWithCode, isTaskVigenteExpired, TASK_UI } from "../constants/taskUiColors";

function parseMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
}

function formatDue(value) {
  if (!value) return "Sin vencimiento";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin vencimiento";
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getOwnerLabel(task, metadata) {
  const owner = String(task?.asignada_a || metadata?.task_owner || "").trim().toLowerCase();
  if (owner === "ana") {
    return task?.agente_nombre || metadata?.assigned_agent_name || "Ana";
  }
  if (metadata?.assigned_agent_name) return metadata.assigned_agent_name;
  if (task?.agente_nombre) return task.agente_nombre;
  if (metadata?.task_owner_label) return metadata.task_owner_label;
  return "Sin asignar";
}

function composeTaskTitleLine(title, displayCode) {
  const t = String(title || "").trim();
  if (!t) return t;
  if (/esperar respuesta/i.test(t)) {
    return displayCode ? `${displayCode} · Esperar respuesta` : t;
  }
  if (!displayCode) return t;
  const code = String(displayCode).trim();
  const upper = t.toUpperCase();
  if (/^T3B1?\s*[·\-–—]/i.test(t)) return t;
  if (upper.startsWith(`${code.toUpperCase()} ·`)) return t;
  if (upper.startsWith(code.toUpperCase())) return t;
  if (/^T2E\s*[·\-–—]/i.test(t) && /^T3B/i.test(code)) {
    return t.replace(/^T2E\s*[·\-–—]\s*/i, `${code} · `);
  }
  return `${displayCode} · ${t}`;
}

export default function TaskCurrentCard({ task, c, isDark, onOpen, compact = false, inline = false, variant = "default", leadEtapa = null, loading = false, leadCreatedAt = null }) {
  // Mientras carga la tarea por primera vez, NO pintar la tarjeta gris "Sin tarea"
  // (eso causaba el parpadeo gris→amarillo al abrir la ficha y daba la falsa
  // sensación de "sin tarea asignada"). No renderizar nada hasta tener la tarea real.
  if (loading && !task && variant === "leadModal") {
    return null;
  }
  const metadata = parseMetadata(task?.metadata);
  // El cliente ya respondió al WhatsApp de espera: la tarea pasa de "esperar" a
  // "evaluar respuesta" (el agente debe abrir el M1/M2). Se refleja en el título.
  const replyPending = (() => {
    const code = String(metadata.task_code || "").toUpperCase();
    if (!["T3B", "T3B1", "T2E", "T4B1", "T4B2", "T4B3"].includes(code)) return false;
    return (
      metadata.whatsapp_reply_pending === true ||
      metadata.whatsapp_reply_pending === "true" ||
      Boolean(metadata.whatsapp_inbound_received_at)
    );
  })();
  const title = replyPending
    ? "Evaluar respuesta"
    : (task
      ? formatTaskTitleWithCode(task, metadata.task_label || task?.tipo || "")
      : (metadata.task_label || task?.tipo || "Sin tarea pendiente"));
  const owner = getOwnerLabel(task, metadata);
  const isOverdue = isTaskVigenteExpired(task, { leadEtapa: leadEtapa || task?.lead_etapa });
  const isUnfinished = Boolean(metadata.display_unfinished) || isOverdue;
  const statusLabel = task ? (isUnfinished ? "No hecha" : "Pendiente") : "Sin tarea";
  const accent = task ? (isOverdue ? "#ef4444" : "#2563eb") : "#64748b";
  const cardBg = isDark ? "#111827" : "#f8fafc";
  const pillText = "#ffffff";
  const highlightBorder = isOverdue ? TASK_UI.noHecha : "#facc15";

  if (variant === "leadModal") {
    const { bg, text, key: categoryKey } = getTaskVigenteLeadModalStyle(task, { leadEtapa: leadEtapa || task?.lead_etapa });
    const typeLabel = isUnfinished ? "No hecha" : getTaskTypeLabel(categoryKey);
    const titleLine = title;
    const cardBorder = categoryKey === "llamada_whatsapp"
      ? "2px solid rgba(0,0,0,0.12)"
      : `2px solid ${bg}`;

    return (
      <div
        title={task?.descripcion || title}
        style={{
          marginTop: 2,
          padding: "4px 8px",
          borderRadius: 8,
          border: cardBorder,
          background: bg,
        }}
      >
        {task ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 800,
                  color: text,
                  lineHeight: 1.2,
                }}
              >
                {titleLine}
              </div>

              {onOpen && !isUnfinished ? (
                <button
                  type="button"
                  onClick={onOpen}
                  style={{
                    padding: "3px 8px",
                    borderRadius: 6,
                    border: isOverdue ? "1px solid rgba(255,255,255,0.65)" : "1px solid #cbd5e1",
                    background: isOverdue ? "rgba(255,255,255,0.95)" : "#ffffff",
                    color: "#0f172a",
                    fontSize: 10,
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Abrir tarea
                </button>
              ) : null}

              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: text,
                  flexShrink: 0,
                  textAlign: "right",
                }}
              >
                {typeLabel}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", fontSize: 9, fontWeight: 700, color: text }}>
              <span>{owner}</span>
              <span>· Vto.: {formatDue(task.fecha_vencimiento)}</span>
              {leadCreatedAt ? <span>· Entrada: {formatDue(leadCreatedAt)}</span> : null}
            </div>
            {task.descripcion && (
              <div style={{
                marginTop: 2,
                fontSize: 9,
                color: text,
                lineHeight: 1.25,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {task.descripcion}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: text, textAlign: "center", fontWeight: 700 }}>
            No hay ninguna tarea principal pendiente.
          </div>
        )}
      </div>
    );
  }

  if (inline) {
    const expiredStyle = isOverdue
      ? { bg: TASK_UI.noHecha, text: "#ffffff", border: TASK_UI.noHecha }
      : { bg: cardBg, text: c.textPrimary, border: highlightBorder };

    return (
      <div
        title={task?.descripcion || title}
        style={{
          padding: "6px 10px",
          borderRadius: 9,
          border: `2px solid ${expiredStyle.border}`,
          background: expiredStyle.bg,
          boxShadow: isDark ? "none" : "0 4px 14px rgba(15,23,42,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <div
            style={{
              minWidth: 0,
              flex: 1,
              fontSize: 11,
              fontWeight: 700,
              color: expiredStyle.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>

          {task && (
            <>
              <span
                style={{
                  padding: "2px 7px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  background: isOverdue ? "rgba(255,255,255,0.22)" : (metadata?.task_owner === "ana" ? "rgba(124,58,237,0.12)" : "rgba(37,99,235,0.12)"),
                  color: isOverdue ? "#ffffff" : pillText,
                  whiteSpace: "nowrap",
                }}
              >
                {owner}
              </span>
              {metadata?.task_code && (
                <span
                  style={{
                    padding: "2px 7px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    background: isOverdue ? "rgba(255,255,255,0.18)" : c.bgInput,
                    color: isOverdue ? "#ffffff" : c.textSecondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {metadata.task_code}
                </span>
              )}
              <span style={{ fontSize: 10, color: isOverdue ? "#ffffff" : c.textMuted, whiteSpace: "nowrap" }}>
                Vence:{" "}
                <span style={{ color: isOverdue ? "#ffffff" : c.textSecondary, fontWeight: 600 }}>
                  {formatDue(task.fecha_vencimiento)}
                </span>
              </span>
            </>
          )}

          {onOpen && task && (
            <button
              type="button"
              onClick={onOpen}
              style={{
                flexShrink: 0,
                height: 24,
                padding: "0 10px",
                borderRadius: 6,
                border: isOverdue ? "1px solid rgba(255,255,255,0.65)" : `1px solid ${accent}33`,
                background: isOverdue ? "rgba(255,255,255,0.95)" : `${accent}12`,
                color: isOverdue ? "#0f172a" : pillText,
                fontSize: 10,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              Abrir tarea
            </button>
          )}

          <span
            style={{
              flexShrink: 0,
              padding: "2px 7px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              background: isOverdue ? "rgba(255,255,255,0.22)" : `${accent}18`,
              color: isOverdue ? "#ffffff" : pillText,
              border: isOverdue ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${accent}33`,
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: compact ? 8 : 10,
        padding: compact ? "10px 12px" : "12px 14px",
        borderRadius: 12,
        border: `2px solid ${highlightBorder}`,
        background: cardBg,
        boxShadow: isDark ? "none" : "0 4px 14px rgba(15,23,42,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: c.textMuted, marginBottom: 6 }}>
            Tarea vigente
          </div>
          <div style={{ fontSize: compact ? 13 : 14, fontWeight: 700, color: c.textPrimary }}>
            {title}
          </div>
        </div>
        <span
          style={{
            flexShrink: 0,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 800,
            background: `${accent}18`,
            color: pillText,
            border: `1px solid ${accent}33`,
          }}
        >
          {statusLabel}
        </span>
      </div>

      {task ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            <span
              style={{
                padding: "3px 8px",
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                background: metadata?.task_owner === "ana" ? "rgba(124,58,237,0.12)" : "rgba(37,99,235,0.12)",
                color: pillText,
              }}
            >
              {owner}
            </span>
            {metadata?.task_code && (
              <span style={{ padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: c.bgInput, color: c.textSecondary }}>
                {metadata.task_code}
              </span>
            )}
          </div>

          <div style={{ marginTop: 8, fontSize: 11, color: c.textMuted }}>
            Vence: <span style={{ color: isOverdue ? "#ef4444" : c.textSecondary, fontWeight: 600 }}>{formatDue(task.fecha_vencimiento)}</span>
          </div>

          {task.descripcion && (
            <div style={{ marginTop: 8, fontSize: 12, color: c.textSecondary, lineHeight: 1.45 }}>
              {task.descripcion}
            </div>
          )}
        </>
      ) : (
        <div style={{ marginTop: 8, fontSize: 12, color: c.textMuted, lineHeight: 1.45 }}>
          No hay ninguna tarea principal pendiente para este lead o cliente.
        </div>
      )}

      {onOpen && task && (
        <button
          type="button"
          onClick={onOpen}
          style={{
            marginTop: 10,
            padding: "7px 12px",
            borderRadius: 8,
            border: `1px solid ${accent}33`,
            background: `${accent}12`,
            color: pillText,
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Abrir tarea
        </button>
      )}
    </div>
  );
}
